import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LegalPages.module.scss';
import { Helmet } from 'react-helmet';

const PrivacyPolicy = () => {
    return (
        <>
            <Helmet>
                <title>Privacy Policy | KeyQuest - XR App Analytics Platform</title>
                <meta name="description" content="KeyQuest's privacy policy explains how we collect, use, and protect your data when using our XR app analytics platform for Meta Quest games." />
                <meta name="robots" content="index, follow" />
            </Helmet>
            
            <div className={styles.legalPage}>
                <div className={styles.container}>
                    <div className={styles.header}>
                        <Link to="/" className={styles.backLink}>
                            ← Back to Home
                        </Link>
                        <h1 className={styles.title}>Privacy Policy</h1>
                        <p className={styles.subtitle}>Last updated: March 18, 2026</p>
                    </div>

                    <div className={styles.content}>
                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>1. Introduction</h2>
                            <p className={styles.text}>
                                Welcome to KeyQuest. We respect your privacy and are committed to protecting your personal data. 
                                This privacy policy explains how we collect, use, and protect your information when you use our 
                                AI-powered XR app analytics platform.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
                            <h3 className={styles.subsectionTitle}>2.1 Account Information</h3>
                            <p className={styles.text}>
                                When you create an account, we collect your name, email address, and password. 
                                We may also collect information from social login providers (Google) when you choose to authenticate through them.
                            </p>
                            
                            <h3 className={styles.subsectionTitle}>2.2 Usage Data</h3>
                            <p className={styles.text}>
                                We collect information about how you use our platform, including:
                            </p>
                            <ul className={styles.list}>
                                <li>Pages visited and time spent on each page</li>
                                <li>Features used and searches performed</li>
                                <li>App tracking and keyword analysis activities</li>
                                <li>Interaction with our AI chatbot and analytics tools</li>
                            </ul>
                            
                            <h3 className={styles.subsectionTitle}>2.3 Technical Data</h3>
                            <p className={styles.text}>
                                We automatically collect technical information including IP address, browser type, 
                                device information, and access logs to ensure platform security and performance.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
                            <p className={styles.text}>We use your information to:</p>
                            <ul className={styles.list}>
                                <li>Provide and maintain our XR analytics services</li>
                                <li>Process your requests and deliver app insights</li>
                                <li>Improve our platform and develop new features</li>
                                <li>Communicate with you about service updates and support</li>
                                <li>Analyze usage patterns to optimize performance</li>
                                <li>Ensure platform security and prevent fraud</li>
                            </ul>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>4. Data Sharing and Third Parties</h2>
                            <p className={styles.text}>
                                We do not sell your personal information. We may share data only in the following circumstances:
                            </p>
                            <ul className={styles.list}>
                                <li><strong>Meta Quest Store Data:</strong> We analyze publicly available app store data to provide analytics</li>
                                <li><strong>Service Providers:</strong> Trusted third parties who help us operate our platform</li>
                                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                            </ul>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>5. Data Security</h2>
                            <p className={styles.text}>
                                We implement industry-standard security measures including encryption, secure servers, 
                                and regular security audits to protect your data. However, no method of transmission over the 
                                internet is 100% secure.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>6. Your Data Rights</h2>
                            <p className={styles.text}>You have the right to:</p>
                            <ul className={styles.list}>
                                <li>Access and update your personal information</li>
                                <li>Delete your account and associated data</li>
                                <li>Export your data in a portable format</li>
                                <li>Opt-out of marketing communications</li>
                                <li>Request information about data processing activities</li>
                            </ul>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>7. Cookies and Tracking</h2>
                            <p className={styles.text}>
                                We use cookies and similar technologies to enhance your experience, remember preferences, 
                                and analyze usage patterns. You can control cookie settings through your browser preferences.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>8. International Data Transfers</h2>
                            <p className={styles.text}>
                                Your data may be transferred to and processed in countries other than your own. 
                                We ensure appropriate safeguards are in place for international data transfers.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>9. Children's Privacy</h2>
                            <p className={styles.text}>
                                Our services are not intended for children under 13. We do not knowingly collect 
                                personal information from children under 13.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>10. Changes to This Policy</h2>
                            <p className={styles.text}>
                                We may update this privacy policy from time to time. We will notify you of any 
                                significant changes by email or by posting a notice on our platform.
                            </p>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>11. Contact Us</h2>
                            <p className={styles.text}>
                                If you have questions about this privacy policy or our data practices, please contact us:
                            </p>
                            <div className={styles.contactInfo}>
                                <p><strong>Email:</strong> privacy@keyquest.io</p>
                                <p><strong>Website:</strong> <a href="https://keyquest.io" className={styles.link}>https://keyquest.io</a></p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PrivacyPolicy;
